import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../services/api'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'

const newTicketSchema = z.object({
  subject: z.string().min(1, 'Subject is required').max(255, 'Subject too long'),
  description: z.string().min(1, 'Description is required'),
  priority: z.enum(['Low', 'Medium', 'High', 'Urgent']).default('Medium'),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  assigneeId: z.string().optional(),
  productId: z.string().optional()
})

type NewTicketForm = z.infer<typeof newTicketSchema>

export const NewTicketPage = () => {
  const { user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [availableUsers, setAvailableUsers] = useState<any[]>([])
  const [availableProducts, setAvailableProducts] = useState<any[]>([])
  const [availableCategories, setAvailableCategories] = useState<any[]>([])
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<NewTicketForm>({
    resolver: zodResolver(newTicketSchema),
    defaultValues: {
      priority: 'Medium',
      tags: []
    }
  })

  useEffect(() => {
    if (user?.role === 'AGENT' || user?.role === 'ADMIN') {
      fetchUsers()
    }
    fetchProducts()
    fetchCategories()
  }, [user?.role])

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users')
      setAvailableUsers(response.data.data || [])
    } catch (err) {
      // Silent error
    }
  }

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products')
      console.log('Products loaded:', response.data.data?.length || 0)
      setAvailableProducts(response.data.data || [])
    } catch (err) {
      console.error('Error loading products:', err)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories')
      console.log('Categories loaded:', response.data.data?.length || 0)
      setAvailableCategories(response.data.data || [])
    } catch (err) {
      console.error('Error loading categories:', err)
    }
  }

  const createTicketMutation = useMutation({
    mutationFn: async (data: NewTicketForm & { attachments: File[] }) => {
      const formData = new FormData()
      
      // Append ticket data
      formData.append('subject', data.subject)
      formData.append('description', data.description)
      formData.append('priority', data.priority)
      
      if (data.category) {
        formData.append('category', data.category)
      }
      
      if (data.tags && data.tags.length > 0) {
        formData.append('tags', JSON.stringify(data.tags))
      }
      
      if (data.assigneeId) {
        formData.append('assigneeId', data.assigneeId)
      }
      
      if (data.productId) {
        formData.append('productId', data.productId)
      }
      
      // Append attachments
      data.attachments.forEach((file) => {
        formData.append('attachments', file)
      })
      
      const response = await api.post('/tickets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    },
    onSuccess: (data) => {
      toast.success('Ticket created successfully!')
      
      // Dispatch event to update ticket count in sidebar
      window.dispatchEvent(new CustomEvent('ticket-created'))
      
      navigate(`/tickets/${data.data.id}`)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error?.message || 'Failed to create ticket'
      toast.error(errorMessage)
    }
  })

  const onSubmit = async (data: NewTicketForm) => {
    console.log('Submitting ticket with data:', data)
    setIsSubmitting(true)
    try {
      await createTicketMutation.mutateAsync({ ...data, attachments })
    } catch (error) {
      console.error('Error submitting ticket:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles = files.filter(file => {
      const maxSize = 10 * 1024 * 1024 // 10MB
      const allowedTypes = ['image/', 'application/pdf', 'text/', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is 10MB.`)
        return false
      }
      
      if (!allowedTypes.some(type => file.type.includes(type.replace('*', '')))) {
        toast.error(`File ${file.name} is not a supported file type.`)
        return false
      }
      
      return true
    })
    
    setAttachments(prev => [...prev, ...validFiles])
  }

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    const files = Array.from(e.dataTransfer.files)
    const validFiles = files.filter(file => {
      const maxSize = 10 * 1024 * 1024 // 10MB
      const allowedTypes = ['image/', 'application/pdf', 'text/', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is 10MB.`)
        return false
      }
      
      if (!allowedTypes.some(type => file.type.includes(type.replace('*', '')))) {
        toast.error(`File ${file.name} is not a supported file type.`)
        return false
      }
      
      return true
    })
    
    setAttachments(prev => [...prev, ...validFiles])
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <i className="fas fa-ticket-alt me-2 text-primary"></i>
          Create New Ticket
        </h2>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left me-2"></i>
          Back
        </Button>
      </div>

      <Row>
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group className="mb-3">
                  <Form.Label>Subject *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Brief description of your issue"
                    {...register('subject')}
                    isInvalid={!!errors.subject}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.subject?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Priority</Form.Label>
                      <Form.Select {...register('priority')}>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  {availableCategories.length > 0 && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Category</Form.Label>
                        <Form.Select {...register('category')}>
                          <option value="">Select a category...</option>
                          {availableCategories
                            .filter((category) => category.isActive)
                            .map((category) => (
                              <option key={category.id} value={category.name}>
                                {category.name}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <Row>
                  {availableProducts.length > 0 && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Product</Form.Label>
                        <Form.Select {...register('productId')}>
                          <option value="">Select a product...</option>
                          {availableProducts
                            .filter((product) => product.isActive)
                            .map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}

                  {user?.role === 'ADMIN' && availableUsers.length > 0 && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Assign To</Form.Label>
                        <Form.Select {...register('assigneeId')}>
                          <option value="">Unassigned</option>
                          {availableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name} ({u.role})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Description *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="Provide detailed information about your issue..."
                    {...register('description')}
                    isInvalid={!!errors.description}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.description?.message}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Include what you were trying to do, what happened, and any error messages you received.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Attachments</Form.Label>
                  <div 
                    className={`border rounded p-3 text-center ${isDragging ? 'border-primary bg-light' : ''}`}
                    style={{ cursor: 'pointer' }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-upload')?.click()}
                  >
                    <i className="fas fa-cloud-upload-alt text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                    <p className="mb-1">Drop files here or click to browse</p>
                    <small className="text-muted">Max 10MB per file • Images, PDF, Word, Text</small>
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf,text/*,.doc,.docx"
                      onChange={handleFileUpload}
                      className="d-none"
                      id="file-upload"
                    />
                  </div>

                  {attachments.length > 0 && (
                    <div className="mt-2">
                      {attachments.map((file, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center justify-content-between bg-light rounded p-2 mb-2"
                        >
                          <div className="d-flex align-items-center">
                            <i className="fas fa-file text-primary me-2"></i>
                            <div>
                              <div className="small">{file.name}</div>
                              <small className="text-muted">{formatFileSize(file.size)}</small>
                            </div>
                          </div>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-danger p-0"
                            onClick={() => removeAttachment(index)}
                          >
                            <i className="fas fa-times"></i>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Attach screenshots or files that help explain your issue.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2 justify-content-end mt-4">
                  <Button 
                    variant="outline-secondary"
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={isSubmitting}
                    onClick={() => console.log('Submit button clicked', errors)}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Create Ticket
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}
